/* WorkTrigHistoryViewRenderer.js */

"use strict";

const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");

class WorkTrigHistoryViewRenderer {
    constructor(model) {
        // this is work trigger grid model
        this._model = model;
        this._gameEditor = this._model.gameEditor;
        this._rootElem = document.createElement("worktrig-history-grid-view-gl");
        this._logger = this._model.logger;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.pagingFeed.on("RefreshView", (this._onFeedRefreshView = this._onFeedRefreshView.bind(this)));
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._historyGrid.on("UserSorted", (this._onUserSorted = this._onUserSorted.bind(this)));
        this._historyGrid.on("UserRowDoubleClicked", (this._openRowLogView = this._openRowLogView.bind(this)));
    }

    _unregisterListeners() {
        this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        this._unregisterGridListeners();
    }

    _unregisterGridListeners() {
        this._historyGrid.removeListener("UserSorted", this._onUserSorted);
        this._historyGrid.removeListener("UserRowDoubleClicked", this._openRowLogView);
    }

    _renderAvailableActions() {
        let numSelected = this.selectedRows.length || 0;
        if (numSelected > 0) {
            this._gameEditor.enableAllActions();
        } else {
            this._gameEditor.disableAllActions();
            this._gameEditor.enableAction(Registrations.WORK_TRIGGER_EXPLORER_ACTION_CREATE);
        }

        if (numSelected > 1) {
            this._gameEditor.disableAction(Registrations.WORK_TRIGGER_EXPLORER_ACTION_CLONE);
        }
    }

    _showDefaultMessageDisplay() {
        return this._gameEditor
            .showDefaultMessageView({
                message: "No history available"
            })
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "WorkTrigHistoryViewRenderer._showDefaultMessageDisplay error");
            });
    }

    _hideDefaultMessageDisplay() {
        return this._gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "WorkTrigHistoryViewRenderer._hideDefaultMessageDisplay error");
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        let shutdowns = [this._historyGrid.shutdown()];
        return Promise.all(shutdowns).then(
            () => {
                this._model = undefined;
                this._gameEditor = undefined;
                this._historyGrid = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "WorkTrigHistoryViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model.whenModelIsReady().then(() => {
            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "DownloadAction",
                handler: (this._onDownloadActionRun = this._onDownloadActionRun.bind(this))
            });

            this._historyGrid = new EntityGrid({
                gridKey: this._model._gridKey,
                logger: this._logger,
                selectable: false,
                columns: this._model.columnConfig,
                pagingFeed: this._model.pagingFeed,
                showHeader: true
            });

            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._historyGrid.renderer.rootElement);
            }
            this._registerListeners();
            this._onFeedRefreshView(); //on load, show default message as if a refresh was done
            this._applyViewSettings(this._gameEditor.getViewSettings());
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onUserSorted(eventData) {
        this._model._onUserSorted(eventData);
    }

    _onFeedRefreshView() {
        let totalEntities = this._model.pagingFeed.totalEntities;
        if (totalEntities === 0) {
            if (!this._defaultViewActive) {
                this._showDefaultMessageDisplay();
            }
            this._hideViewActions();
        } else {
            if (this._defaultViewActive) {
                this._hideDefaultMessageDisplay();
            }
            this._showViewActions();
        }
    }

    _onDownloadActionRun() {
        this._model._gameEditor.disableViewAction("DownloadAction");
        this._model.downloadTriggerReport().finally(() => {
            this._model._gameEditor.enableViewAction("DownloadAction");
        });
    }

    _openRowLogView(row) {
        const triggerEntity = this._model._pagingFeed.getEntityById(row.selectedRow);
        if (!triggerEntity) {
            return this._logger.warn("No Trigger Entity on row: " + JSON.stringify(row));
        }
        if (!triggerEntity.workId) {
            return this._logger.warn("No Work for Trigger Entity on row: " + JSON.stringify(row));
        }

        return this._model._gameEditor.setActiveView(
            Registrations.WTE_VIEW_LOGS,
            [
                {
                    value: {
                        fqn: Registrations.WTE_VIEW_HISTORY
                    },
                    content: "Schedule History",
                    left: {
                        arrow: {
                            direction: "left"
                        }
                    }
                }
            ],
            {
                triggerEntity
            }
        );
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _showViewActions() {
        this._model.gameEditor.showViewAction("DownloadAction");
    }

    _hideViewActions() {
        this._model.gameEditor.hideViewAction("DownloadAction");
    }

    _applyViewSettings(viewSettings) {
        viewSettings = viewSettings || {};
        if (viewSettings.triggerId) {
            return this._historyGrid.pagingFeed.ensureEntityOnPage(viewSettings.triggerId).then((triggerEntity) => {
                this._historyGrid.setSelectedRows([triggerEntity.id]);
                if (viewSettings.action && viewSettings.action === "resultReport") {
                    this._model.downloadWorkReport(triggerEntity);
                }
            });
        }
        return Promise.resolve();
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = WorkTrigHistoryViewRenderer;
